import { PerkShape } from "~shapes/perk"
import { useState } from "react"
import { QueryObserverResult, useMutation } from "react-query"
import { PERK_REDEMPTION_TYPE } from "~constants/perks"
import useRedemptionFlow from "~hooks/useRedemptionFlow"
import RedemptionService from "~services/RedemptionService"
import { ActionType, RedemptionFlow } from "~shapes/redemption"

export enum FORM_TYPES {
  SHIPPABLE = "Shippable",
  DYNAMIC = "Dynamic",
}

export interface FormProps {
  type: FORM_TYPES
  formId: string
}

export default function useRedeemLogic(
  perk: PerkShape | undefined,
  redemptionType: PERK_REDEMPTION_TYPE,
  userId: string,
) {
  const [isStartingProcess, setIsStartingProcess] = useState<boolean>(false)
  const { refetch: fetchRedemptionFlow } = useRedemptionFlow(perk?.code || "")
  const perkId = perk?.id || ""
  const { mutateAsync: redeem, isLoading } = useMutation(
    "redeem",
    ({ perkId, payload }: { perkId: string; payload: any }) =>
      RedemptionService.redeemPerk(perkId, payload),
  )

  const redeemAction = async (id: string, payload: any) => {
    const response = await redeem({ perkId: id, payload })
    setIsStartingProcess(false)
    return response
  }

  const redeemTypeSimple = async () => {
    const flow: QueryObserverResult<RedemptionFlow> =
      await fetchRedemptionFlow()
    if (flow.data && flow.data.steps) {
      return proceedWithSteps(flow.data)
    } else {
      return await redeemAction(perkId, { userId: userId })
    }
  }

  const proceedWithSteps = (redemptionFlow: RedemptionFlow) => {
    const step = redemptionFlow.steps[0]
    switch (step.action) {
      case ActionType.FILL_FORM:
        return completeForm(FORM_TYPES.DYNAMIC, step.formId)
    }
  }

  const completeForm = (type: FORM_TYPES, formId?: string) => {
    const response: FormProps = {
      type: type,
      formId: formId || "",
    }
    setIsStartingProcess(false)
    return response
  }

  const startRedeemProcess = async () => {
    if (!perkId) return
    setIsStartingProcess(true)
    switch (redemptionType) {
      case PERK_REDEMPTION_TYPE.ExternalLink:
        return await redeemAction(perkId, { userId: userId })
      case PERK_REDEMPTION_TYPE.CouponList:
        return await redeemAction(perkId, { userId: userId })
      case PERK_REDEMPTION_TYPE.Simple:
        return await redeemTypeSimple()
      case PERK_REDEMPTION_TYPE.Shippable:
        return completeForm(FORM_TYPES.SHIPPABLE)
      case PERK_REDEMPTION_TYPE.Passport:
        return completeForm(FORM_TYPES.SHIPPABLE)
    }
  }

  return {
    startRedeemProcess,
    redeemAction,
    isRedeeming: isLoading,
    isStartingProcess,
    redemptionType,
  }
}
